import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../context/AuthContext.js'
import VersionHistoryPanel from '../components/VersionHistoryPanel.jsx'
import './Destinations.css'

// Matches the language list the dashboard uses everywhere else for translated content.
const LANGUAGES = [
  ['de','Deutsch'],['en','English'],['es','Español'],['it','Italiano'],
  ['pl','Polski'],['nl','Nederlands'],['da','Dansk'],['fr','Français'],
  ['hu','Magyar'],['hr','Hrvatski'],['pt','Português'],['cs','Čeština'],
  ['bg','Български'],['et','Eesti'],['fi','Suomi'],['el','Ελληνικά'],
  ['lv','Latviešu'],['lt','Lietuvių'],['no','Norsk'],['ru','Русский'],
  ['sr','Srpski'],['sk','Slovenčina'],['sl','Slovenščina'],['sv','Svenska'],
  ['tr','Türkçe'],['uk','Українська'],
]

const KINDS = [
  ['ramp',     'Rampe'],
  ['waiting',  'Wartebereich'],
  ['parking',  'Stellplatz'],
  ['gate',     'Tor'],
  ['building', 'Gebäude'],
  ['other',    'Sonstiges'],
]

/**
 * Why an assignment from an earlier day is still open, in admin-facing wording.
 *
 * Shown as a tooltip rather than a warning, because in these cases it is the correct
 * behaviour: nothing is released automatically unless the site both closes overnight
 * and closes its visits automatically.
 */
function describeCarryOver(reason) {
  switch (reason) {
    case 'no_standby_no_checkout':
      return 'Kein Standby-Zeitfenster und kein automatischer Mitternachts-Checkout hinterlegt — ' +
             'Zuweisungen über Nacht bleiben bestehen und werden nur manuell freigegeben.'
    case 'no_standby_window':
      return 'Für diesen Standort ist kein Standby-Zeitfenster hinterlegt, der Betrieb läuft also ' +
             'durchgehend. Eine Zuweisung über Nacht ist hier normal.'
    case 'no_midnight_checkout':
      return 'Der automatische Mitternachts-Checkout ist deaktiviert, daher wird auch kein Zielort ' +
             'automatisch freigegeben.'
    default:
      return 'Zuweisung aus einem früheren Tag, noch nicht freigegeben.'
  }
}

const EMPTY_FORM = {
  name: '', kind: 'ramp', assignmentMode: 'exclusive', infoPageId: '',
  capacity: '1', warnAt: '', defaultMinutes: '', active: true, sortOrder: 0,
}

/**
 * Destination (Zielort) configuration — ramps, waiting areas, gates.
 *
 * This is the settings screen: creating places and setting their capacities. The
 * day-to-day board where staff assign visitors to them is a separate page, because
 * the two have different audiences — configuring is an admin act, assigning is
 * reception's daily work.
 */
export default function Destinations() {
  const { admin, isOwner, apiFetch } = useAuth()

  const [tenants,  setTenants]  = useState([])
  const [tenantId, setTenantId] = useState('')
  const [items,    setItems]    = useState([])
  const [pages,    setPages]    = useState([])
  const [loading,  setLoading]  = useState(true)

  const [editing,    setEditing]    = useState(null)   // null = nothing open, 'new' = create
  const [form,       setForm]       = useState(EMPTY_FORM)
  const [sourceLang, setSourceLang] = useState('de')
  const [saving,     setSaving]     = useState(false)
  const [error,      setError]      = useState(null)
  const [notice,     setNotice]     = useState(null)

  useEffect(() => {
    if (!isOwner) { setTenantId(admin?.tenant_id ?? ''); return }
    apiFetch('/api/admin/tenants')
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : []
        setTenants(list)
        setTenantId(prev => prev || list[0]?.id || '')
      })
      .catch(() => {})
  }, [isOwner, admin, apiFetch])

  const load = useCallback(async () => {
    if (!tenantId) { setLoading(false); return }
    setLoading(true)
    try {
      // Inactive ones are included deliberately: a destination is deactivated rather
      // than deleted once it has history, and it must stay editable afterwards.
      const [dRes, pRes] = await Promise.all([
        apiFetch(`/api/admin/destinations/${tenantId}?includeInactive=1`),
        apiFetch(`/api/admin/info-pages/${tenantId}`),
      ])
      if (dRes.ok) setItems(await dRes.json())
      if (pRes.ok) setPages(await pRes.json())
    } catch {
      setError('Zielorte konnten nicht geladen werden.')
    } finally {
      setLoading(false)
    }
  }, [tenantId, apiFetch])

  useEffect(() => { load() }, [load])

  const openNew = () => {
    setEditing('new'); setForm(EMPTY_FORM); setError(null); setNotice(null)
  }

  const openEdit = (d) => {
    setEditing(d.id)
    setForm({
      name:           d.displayName ?? '',
      kind:           d.kind,
      assignmentMode: d.assignment_mode,
      infoPageId:     d.info_page_id ?? '',
      // Empty string rather than '0' for an unlimited capacity — the field means
      // "no limit" when blank, and 0 would make it permanently full.
      capacity:       d.capacity ?? '',
      warnAt:         d.warn_at ?? '',
      defaultMinutes: d.default_minutes ?? '',
      active:         d.active,
      sortOrder:      d.sort_order,
    })
    setError(null); setNotice(null)
  }

  const close = () => { setEditing(null); setError(null) }

  const set = (patch) => setForm(prev => ({ ...prev, ...patch }))

  const isShared = form.assignmentMode === 'shared'

  const save = async () => {
    if (!form.name.trim()) { setError('Bezeichnung ist erforderlich'); return }
    setSaving(true); setError(null); setNotice(null)
    try {
      const body = { ...form, name: form.name.trim(), sourceLang, infoPageId: form.infoPageId || null }
      const res = await apiFetch(
        editing === 'new'
          ? `/api/admin/destinations/${tenantId}`
          : `/api/admin/destinations/${tenantId}/${editing}`,
        { method: editing === 'new' ? 'POST' : 'PUT', body: JSON.stringify(body) }
      )
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'Speichern fehlgeschlagen')
      setEditing(null)
      await load()
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const remove = async (d) => {
    if (!window.confirm(`"${d.displayName}" entfernen?`)) return
    setError(null); setNotice(null)
    try {
      const res = await apiFetch(`/api/admin/destinations/${tenantId}/${d.id}`, { method: 'DELETE' })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'Entfernen fehlgeschlagen')
      // Deactivated rather than deleted when assignments reference it — say so, or it
      // looks as though the delete silently failed.
      if (data.deactivated) {
        setNotice('Zielort wurde deaktiviert statt gelöscht, da bereits Zuweisungen darauf verweisen. ' +
                  'Die Belegungshistorie bleibt damit auswertbar.')
      }
      await load()
    } catch (err) {
      setError(err.message)
    }
  }

  const kindLabel = (kind) => KINDS.find(([k]) => k === kind)?.[1] ?? kind
  const pageName  = (id) => pages.find(p => p.id === id)?.name ?? null

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Zielorte</h1>
          <p>
            Rampen, Wartebereiche und Tore, zu denen Besucher geschickt werden können.
            Die Zuweisung im Tagesbetrieb erfolgt auf der Disposition.
          </p>
        </div>
        <button className="btn btn-primary" onClick={openNew} disabled={!tenantId}>
          + Neuer Zielort
        </button>
      </div>

      {isOwner && tenants.length > 0 && (
        <div className="card dst-tenant-card">
          <div className="form-group dst-tenant-form-group">
            <label>Mandant</label>
            <select className="form-control" value={tenantId} onChange={e => setTenantId(e.target.value)}>
              {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        </div>
      )}

      {error  && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-warning">{notice}</div>}

      {editing && (
        <div className="card dst-form-card">
          <div className="section-title">
            {editing === 'new' ? 'Neuer Zielort' : 'Zielort bearbeiten'}
          </div>

          <div className="dst-form-grid">
            <div className="form-group dst-span-2">
              <label>Bezeichnung</label>
              <input
                className="form-control"
                value={form.name}
                onChange={e => set({ name: e.target.value })}
                placeholder="z. B. Rampe 4"
              />
              {/* The name is shown to the visitor, so it is translated on save like
                  info-page text — "Wartebereich Nord" means nothing to a Polish driver. */}
              <span className="dst-hint">
                Wird beim Speichern automatisch übersetzt und dem Besucher angezeigt.
              </span>
            </div>

            <div className="form-group">
              <label>Eingabesprache</label>
              <select className="form-control" value={sourceLang} onChange={e => setSourceLang(e.target.value)}>
                {LANGUAGES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label>Art</label>
              <select className="form-control" value={form.kind} onChange={e => set({ kind: e.target.value })}>
                {KINDS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
              </select>
            </div>

            <div className="form-group dst-span-2">
              <label>Zuweisungsart</label>
              <select
                className="form-control"
                value={form.assignmentMode}
                onChange={e => set({ assignmentMode: e.target.value })}
              >
                <option value="exclusive">Exklusiv — ein Platz gehört einer Person</option>
                <option value="shared">Geteilt — es wird gegen die Kapazität gezählt</option>
              </select>
              <span className="dst-hint">
                {isShared
                  ? 'Mehrere Besucher gleichzeitig sind der Normalfall (Wartebereich, Parkplatz). Keine Zeitvorgabe, keine Rückfrage beim Zuweisen.'
                  : 'Ein Platz ist namentlich belegt (Rampe, Waage). Zeitvorgabe und Rückfrage „freigeben und neu zuweisen?" gelten nur hier.'}
              </span>
            </div>

            <div className="form-group">
              <label>Kapazität</label>
              <input
                className="form-control"
                type="number"
                min="1"
                value={form.capacity}
                onChange={e => set({ capacity: e.target.value })}
                placeholder="leer = unbegrenzt"
              />
              <span className="dst-hint">
                Leer bedeutet unbegrenzt — ein solcher Zielort wird nie „voll" und eignet
                sich als sicherer Abschluss einer Regelkette.
              </span>
            </div>

            {isShared ? (
              <div className="form-group">
                <label>Warnung ab</label>
                <input
                  className="form-control"
                  type="number"
                  min="1"
                  value={form.warnAt}
                  onChange={e => set({ warnAt: e.target.value })}
                  placeholder="z. B. 8"
                />
                <span className="dst-hint">
                  Meldung an das Personal, sobald so viele Plätze belegt sind.
                </span>
              </div>
            ) : (
              <div className="form-group">
                <label>Vorgabedauer (Minuten)</label>
                <input
                  className="form-control"
                  type="number"
                  min="1"
                  value={form.defaultMinutes}
                  onChange={e => set({ defaultMinutes: e.target.value })}
                  placeholder="optional"
                />
                <span className="dst-hint">
                  Nur ein Startwert: die zuweisende Person legt die Dauer je Zuweisung fest.
                </span>
              </div>
            )}

            <div className="form-group dst-span-2">
              <label>Informationsseite</label>
              <select
                className="form-control"
                value={form.infoPageId}
                onChange={e => set({ infoPageId: e.target.value })}
              >
                <option value="">— keine (Seite wird aus der Bezeichnung erzeugt) —</option>
                {pages.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <span className="dst-hint">
                Inhalt, den der Besucher sieht. Ohne Seite wird „Ihr Ziel: {form.name || 'Rampe 4'}" angezeigt.
              </span>
            </div>

            <div className="form-group">
              <label>Reihenfolge</label>
              <input
                className="form-control"
                type="number"
                value={form.sortOrder}
                onChange={e => set({ sortOrder: Number(e.target.value) || 0 })}
              />
            </div>

            <div className="form-group dst-checkbox-group">
              <label className="dst-checkbox-label">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={e => set({ active: e.target.checked })}
                />
                Aktiv
              </label>
              <span className="dst-hint">Inaktive Zielorte können nicht zugewiesen werden.</span>
            </div>
          </div>

          <div className="dst-form-actions">
            <button className="btn btn-primary" onClick={save} disabled={saving}>
              {saving ? 'Speichern…' : 'Speichern'}
            </button>
            <button className="btn btn-ghost" onClick={close} disabled={saving}>Abbrechen</button>
          </div>
        </div>
      )}

      <div className="card">
        <div className="section-title dst-title">
          Zielorte
          <span className="dst-badge">{items.length}</span>
        </div>

        {loading ? (
          <div className="loading-state"><span className="spinner" /></div>
        ) : items.length === 0 ? (
          <p className="dst-empty">
            Noch keine Zielorte angelegt. Ein Zielort ist eine physische Anlaufstelle —
            eine Rampe, ein Wartebereich, ein Tor.
          </p>
        ) : (
          <div className="dst-list">
            {items.map(d => (
              <div key={d.id} className={`dst-row${d.active ? '' : ' dst-row--inactive'}`}>
                <div className="dst-main">
                  <span className="dst-name">{d.displayName}</span>
                  <span className="dst-meta">
                    <span className="dst-kind">{kindLabel(d.kind)}</span>
                    <span className={`dst-mode dst-mode--${d.assignment_mode}`}>
                      {d.assignment_mode === 'shared' ? 'geteilt' : 'exklusiv'}
                    </span>
                    {pageName(d.info_page_id)
                      ? <span className="dst-page">→ {pageName(d.info_page_id)}</span>
                      : <span className="dst-page dst-page--none">ohne Informationsseite</span>}
                    {!d.active && <span className="dst-inactive-tag">inaktiv</span>}
                  </span>
                </div>

                {/* Live state, even on the settings page: it is the quickest way to see
                    that a capacity was set too low. */}
                <div className="dst-state">
                  <span className={`dst-occupancy${d.full ? ' dst-occupancy--full' : ''}`}>
                    {d.occupied}{d.capacity != null ? ` / ${d.capacity}` : ' / ∞'}
                  </span>
                  {d.reserved > 0 && <span className="dst-reserved">{d.reserved} vorgemerkt</span>}
                  {/* Carried over from an earlier day. Only a problem where the site
                      actually closes overnight — a yard taking deliveries around the
                      clock has these as a matter of course, and flagging them there
                      would train staff to ignore the flag. */}
                  {d.carriedOver > 0 && (
                    d.overdueCarryOver ? (
                      <span
                        className="dst-carryover dst-carryover--overdue"
                        title="Zuweisung aus einem früheren Tag — der Standort schließt über Nacht, sie hätte zum Tagesende freigegeben werden müssen."
                      >
                        {d.carriedOver} überfällig
                      </span>
                    ) : (
                      <span
                        className="dst-carryover"
                        title={describeCarryOver(d.endOfDayRelease)}
                      >
                        {d.carriedOver} aus Vortag
                      </span>
                    )
                  )}
                  {d.pressure === 'warn' && <span className="dst-pressure">Schwellwert erreicht</span>}
                  {d.pressure === 'full' && <span className="dst-pressure dst-pressure--full">voll</span>}
                </div>

                <div className="dst-actions">
                  <button className="btn btn-ghost btn-sm" onClick={() => openEdit(d)}>Bearbeiten</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => remove(d)}>Entfernen</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {tenantId && (
        <VersionHistoryPanel table="destinations" tenantId={tenantId} onRestored={load} />
      )}
    </div>
  )
}
